/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React, { useState } from "react";
import * as messagesActions from "@/src/store/messages/actions";
import _merge from "lodash/merge";
import _filter from "lodash/filter";
import FileUploadForm, { FileUploadFormProps } from "@/src/components/Form/Common/FileUploadSection/FileUploadForm";
import { fileCheck, saveMediaObject } from "@/src/services/formServiceFunctions";
import _last from "lodash/last";
import _head from "lodash/head";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createMediaObject } from "@/src/services/reactQuery/reactQueryMediaObjectService";
import { queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { MediaObject } from "@/src/types/Entities/MediaObject/MediaObject";

type FileUploadSectionProps = {
  setIsBlocked: Function;
  fieldname: string;
  description: string;
  processCostSupport: Record<string, any>;
  isBlocked: boolean;
} & Pick<FileUploadFormProps, "headline" | "helperText">;
const FileUploadSection = ({ setIsBlocked, fieldname, description, processCostSupport, isBlocked, ...rest }: FileUploadSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filePath, setFilePath] = useState("");
  const queryClient = useQueryClient();

  const updatePCSMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.item("/process_cost_supports", processCostSupport.id),
      }),
  });
  const createMediaObjectMutation = useMutation({
    mutationFn: createMediaObject,
  });

  const mediaObjectCollection: MediaObject[] = processCostSupport.receipts[fieldname] || [];

  const resetState = () => {
    setFilePath("");
    setIsLoading(false);
    setIsBlocked(false);
  };

  const handleChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (mediaObjectCollection.length > 0 && (target.files?.[0].type === "application/pdf" || _last(mediaObjectCollection)?.mimeType === "application/pdf")) {
      messagesActions.addMessage({
        type: "warning",
        text: "Die PDF-Datei kann nicht hochgeladen werden. Sie können entweder ein mehrseitiges PDF hochladen, oder mehrere einzelne Bilder als JPG oder PNG.",
      });
      resetState();
    }

    const fileCheckRes = fileCheck(target);
    if (fileCheckRes.hasError === true) {
      messagesActions.addMessage({ type: "error", text: fileCheckRes.errorMsg });
      return;
    }

    const file = _head(target.files);
    if (!file) {
      return;
    }
    setFilePath(file.name);
    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setIsLoading(true);
    setIsBlocked(true);

    const mediaObject = await saveMediaObject(createMediaObjectMutation, {
      file: file,
      fieldname: fieldname,
      description: description,
      product: "processCostSupport",
      productId: processCostSupport.id,
    });

    if (mediaObject === false) {
      resetState();
      return;
    }

    const receipts = _merge({}, processCostSupport.receipts);
    if (!receipts[fieldname]) {
      receipts[fieldname] = [];
    }
    mediaObject.thumbnail = null;
    receipts[fieldname].push(mediaObject);

    await updatePCSMutation.mutateAsync({
      uri: "/process_cost_supports",
      id: processCostSupport.id,
      data: {
        receipts: receipts,
      },
    });
    resetState();
  };

  const deleteMediaObject = (mediaObjectToDelete: MediaObject) => {
    let receipts = _merge({}, processCostSupport.receipts);
    receipts[fieldname] = _filter(receipts[fieldname], (mediaObject) => {
      return mediaObject.id !== mediaObjectToDelete.id;
    });
    updatePCSMutation.mutate({
      uri: "/process_cost_supports",
      id: processCostSupport.id,
      data: {
        receipts: receipts,
      },
    });
  };

  const isPdfUploaded = () => {
    return mediaObjectCollection.length > 0 && _head(mediaObjectCollection)?.mimeType === "application/pdf";
  };

  return (
    <FileUploadForm
      mediaObjectCollection={mediaObjectCollection}
      deleteMediaObject={deleteMediaObject}
      handleFileChange={handleChange}
      fieldname={fieldname}
      allowMore={!isPdfUploaded()}
      filePath={filePath}
      isBlocked={isBlocked}
      {...rest}
      headlineVariant="h6"
      headlineColor="text.primary"
    />
  );
};
export default FileUploadSection;
